import * as _ from 'lodash';
import { AppStateService } from 'src/app/modules/core/services/app-state.service';
import { VehicleActivity } from 'src/app/modules/shared/models';
import { VehicleDeparture } from 'src/app/modules/shared/models/vehicle/vehicle-departure';
import { VehicleDeviation } from 'src/app/modules/shared/models/vehicle/vehicle-deviation';
import { ActivityPointListItem } from './activity-point-list-item';

export class VehicleTripInfoHelper {

    public static getActivityPointItems(
        showAll: boolean,
        vehicleActivity: VehicleActivity,
        departureDeviation: VehicleDeviation,
        arrivalDeviation: VehicleDeviation,
        appState: AppStateService,
        nextStopsCount: number = 3
    ) {
        let deviationMatched = false;

        const items = _.map(vehicleActivity?.activityPoints, ap => {
            const activityPointItem = new ActivityPointListItem({
                activityPoint: ap
            });
            return activityPointItem;
        });

        if (departureDeviation) {
            const lastSequenceNumber = _.maxBy(items, item => item.activityPoint.sequenceNumber)?.activityPoint?.sequenceNumber;

            // we match departed from both on stopRef (for trafficareas using TDS) and on name (traffic areas using OMS)
            const departedFromStop = _.find(items, item => ((item.activityPoint.stopId === departureDeviation.stopRef
                || item.activityPoint.stopDescription?.indexOf(departureDeviation?.stopName) >= 0
                || departureDeviation?.stopName?.indexOf(item.activityPoint.stopDescription) >= 0)
                && departureDeviation.sequenceNumber === item.activityPoint.sequenceNumber));
            if (departedFromStop) {
                departedFromStop.deviation = departureDeviation;

                const nextStop = _.find(items, item =>
                    item.activityPoint.sequenceNumber === departedFromStop.activityPoint.sequenceNumber + 1);

                const arrivedAtStop = _.find(items, item => ((item.activityPoint.stopId === arrivalDeviation?.stopRef
                    || item.activityPoint.stopDescription?.indexOf(arrivalDeviation?.stopName) >= 0
                    || arrivalDeviation?.stopName?.indexOf(item.activityPoint.stopDescription) >= 0)
                    && arrivalDeviation?.sequenceNumber === item.activityPoint.sequenceNumber));

                if (arrivedAtStop?.activityPoint.sequenceNumber === nextStop?.activityPoint.sequenceNumber) {
                    nextStop.hasArrived = true;
                } else if (nextStop) {
                    nextStop.arrivesTo = true;
                }

                _.each(items, item => {
                    if (item.activityPoint.sequenceNumber <= departedFromStop.activityPoint.sequenceNumber) {
                        item.hasDeparted = true;

                        // can't use stopId since OMS has stopId (number) instead of placeId (guid)
                        const departedHistories = _.filter(departureDeviation?.departures, departure =>
                            departure.sequenceNumber ===
                            item.activityPoint.sequenceNumber
                            && departure.sequenceNumber
                            !== departedFromStop.activityPoint.sequenceNumber);

                        const departedHistory = this._getOneMatchedDepartedHistory(departedHistories);

                        if (departedHistory) {

                            // calculate deviation color and text.
                            const config = appState.stateSnapshot.punctualityConfig.getConfig(departedHistory.deviation.getTotalSeconds());
                            item.deviation = new VehicleDeviation({
                                deviation: departedHistory.deviation,
                                color: config.color,
                                text: config.text
                            });
                        }
                    }

                    // Hide stops not in focus.
                    if (item.activityPoint.sequenceNumber < departedFromStop.activityPoint.sequenceNumber - 1 ||
                        item.activityPoint.sequenceNumber > nextStop?.activityPoint.sequenceNumber + (nextStopsCount - 1)) {
                        item.visible = showAll;
                    }

                    // Always show first stop
                    if (item.activityPoint.sequenceNumber === 1) {
                        item.isFirst = true;
                        item.visible = true;
                    }

                    // Always show last stop
                    if (item.activityPoint.sequenceNumber === lastSequenceNumber) {
                        item.visible = true;
                        item.isLast = true;
                    }
                });
                deviationMatched = true;
            } else {
                deviationMatched = false;
            }
        } else if (arrivalDeviation) {

            const lastSequenceNumber = _.maxBy(items, item => item.activityPoint.sequenceNumber)?.activityPoint?.sequenceNumber;

            const arrivedAtStop = _.find(items, item => ((item.activityPoint.stopId === arrivalDeviation?.stopRef
                || item.activityPoint.stopDescription?.indexOf(arrivalDeviation?.stopName) >= 0)
                && arrivalDeviation?.sequenceNumber === item.activityPoint.sequenceNumber));

            if (arrivedAtStop) {
                _.each(items, item => {
                    if (item.activityPoint.sequenceNumber < arrivedAtStop.activityPoint.sequenceNumber) {
                        item.hasDeparted = true;
                    }

                    if (item.activityPoint.sequenceNumber === arrivedAtStop.activityPoint.sequenceNumber) {
                        item.hasArrived = true;
                    }

                    // Hide stops not in focus.
                    if (item.activityPoint.sequenceNumber < arrivedAtStop.activityPoint.sequenceNumber ||
                        item.activityPoint.sequenceNumber > arrivedAtStop.activityPoint.sequenceNumber + (nextStopsCount - 1)) {
                        item.visible = showAll;
                    }

                    // Always show first stop
                    if (item.activityPoint.sequenceNumber === 1) {
                        item.isFirst = true;
                        item.visible = true;
                    }

                    // Always show last stop
                    if (item.activityPoint.sequenceNumber === lastSequenceNumber) {
                        item.visible = true;
                        item.isLast = true;
                    }
                });
                deviationMatched = true;
            } else {
                deviationMatched = false;
            }
        }

        return { items, deviationMatched };
    }

    private static _getOneMatchedDepartedHistory(vehicleDepartures: VehicleDeparture[]): VehicleDeparture {
        if (!vehicleDepartures || vehicleDepartures.length === 0) {
            return null;
        }

        if (vehicleDepartures.length === 1) {
            return vehicleDepartures[0];
        }

        // If more than one departure deviation matches an activity point, choose the lastest departed one
        return _.minBy(vehicleDepartures, vehicleDeparture => vehicleDeparture.deviation.getTotalSeconds());
    }
}
